import { useEffect, useRef, useState } from "react";

import { useRouteMatch } from "react-router-dom";

import { setFavorites } from "@/api/endpoints/favorite";
import { postTrackingData } from "@/api/endpoints/track";
import DotsHorizontal from "@/assets/icons/dots-horizontal.svg";
import FavoriteIcon from "@/assets/icons/favorite.svg";
import {
  INSTAGRAM_TITLE,
  MentionType,
  NON_AUDIO_MEDIA,
  SOURCE,
  Source,
} from "@/constants";
import { useEpisodes } from "@/contexts/EpisodesContext";
import { useI18n } from "@/contexts/i18nContext";
import { useTags } from "@/contexts/TagContext";
import useHash from "@/hooks/useHash";
import { ShareIcon } from "@/Icons";
import { EVENT } from "@/tracking_events";
import { cx, isSourceWithTitle } from "@/utils";
import { extractMentionTrackingPayload } from "@/utils/tracking";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";
import ExpandDescription from "@components/ExpandDescription";
import TextButton from "@components/TextButton";
import Typography from "@components/Typography";

import SourceDisplay from "../../../SourceDisplay";
import { useEpisode } from "../../context";
import { getShowName } from "../../utils/getShowName";
import Cover from "../Cover";
import AudioBodyMobile from "./AudioBodyMobile";
import TextBodyMobile from "./TextBodyMobile";

import styles from "./EpisodeMobile.module.css";

const isTextBody = (source: Source, context: MentionType, title?: string) => {
  // text posts ("meta" for SoMe) are always text
  if (context === "text_post") {
    return true;
  }

  // instagram gets different components if video or text
  if (source === SOURCE.INSTAGRAM) {
    return title !== INSTAGRAM_TITLE.VIDEO;
  }

  // non-audio media gets text
  if (NON_AUDIO_MEDIA.includes(source)) {
    return true;
  }

  // rest gets audio
  return false;
};

const renderBody = (
  source: Source,
  context: MentionType,
  defaultOpen: boolean,
  title?: string
) =>
  isTextBody(source, context, title) ? (
    <TextBodyMobile defaultOpen={defaultOpen} />
  ) : (
    <AudioBodyMobile defaultOpen={defaultOpen} />
  );

type EpisodeMobileProps = {
  className?: string;
};

const EpisodeMobile: React.FC<EpisodeMobileProps> = ({ className }) => {
  const episode = useEpisode();
  const {
    title,
    source,
    website_url,
    published_date,
    external_source,
    mentions,
    is_paywalled,
    is_favorited,
    description_has_mention,
    description_mention,
    trimmed_description_mention,
    title_mention_uuid,
    description_mention_uuid,
    show,
    hideActions,
  } = episode;
  const { share } = useEpisodes();
  const { formatDate } = useI18n();
  const { fetchOrganizationTags } = useTags() || {
    organizationTags: [],
  };
  // expand mentions if public view
  const defaultOpen = useRouteMatch("/(sv|en)/m/:slug--:uuid");

  // If a hash is present in the URL, focuses the matching mention
  const episodeRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);
  const hash = useHash();
  useEffect(() => {
    const mention_uuids: (string | null)[] =
      mentions?.map((m) => m.public_id) || [];
    // adding meta-hits to uuids-array, if any.
    mention_uuids.push(title_mention_uuid, description_mention_uuid);

    if (mention_uuids.includes(hash) && episodeRef.current) {
      // Offset scrolling by a timeout to allow the page to render first.
      // Does not work in chrome without it.
      setTimeout(() => {
        if (episodeRef.current) {
          episodeRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 50);

      setHighlight(true);

      const t = setTimeout(() => {
        setHighlight(false);
      }, 3500);

      return () => clearTimeout(t);
    }
  }, [hash, description_mention_uuid, title_mention_uuid, mentions]);

  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isFavorited, setIsFavorited] = useState(is_favorited);

  const onMarkFavorite = () => {
    if (!isFavorited) {
      postTrackingData(
        EVENT.MARK_FAVOURITE,
        extractMentionTrackingPayload(episode)
      );
    }

    setIsFavorited((v) => !v);

    setFavorites(
      episode.mentions.map((m) => m.public_id),
      !isFavorited
    )
      .then(() => {
        fetchOrganizationTags();
      })
      .catch((error) => {
        throw error;
      });
  };

  return (
    <div
      ref={episodeRef}
      className={cx(styles.root, { [styles.highlight]: highlight }, className)}
      data-testid="mobile-episode-container"
    >
      <div className={styles.header}>
        <Cover episode={episode} className={styles.cover} />

        <div className={styles.info}>
          <div className={styles.row}>
            <div className={styles.timestamp}>
              <SourceDisplay
                title={title}
                variant="body-2"
                className={styles.source}
                noIcon
                source={source}
                external_source={external_source}
              />
              {", "}
              {formatDate(published_date, "PPP")}
            </div>
          </div>

          <Typography
            className={cx(styles.title, "highlight-match")}
            block
            data-testid="show-title"
            variant="subtitle-1"
            {...(website_url
              ? {
                  component: "a",
                  href: website_url,
                  target: "_blank",
                  rel: "noreferrer",
                  // onClick: () => track("show"),
                }
              : {})}
          >
            {getShowName({ source, show })}
            {isSourceWithTitle(source) && (
              <>
                {": "}
                <span dangerouslySetInnerHTML={{ __html: title }} />
              </>
            )}
          </Typography>
        </div>
        {!hideActions && (
          <div className={styles.actions}>
            <Dropdown>
              <DropdownToggle>
                <TextButton
                  className="unstyle-dropdown"
                  data-testid="mention-action-menu-toggler"
                  icon={DotsHorizontal}
                />
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem
                  className={cx(styles.item, { favorited: isFavorited })}
                  data-testid="mark-favorite-button-mobile"
                  onClick={onMarkFavorite}
                >
                  <FavoriteIcon
                    className={cx(styles.icon, {
                      [styles.favorited]: isFavorited,
                    })}
                  />{" "}
                  {L.MENTIONS_MARK_AS_FAVORITE}
                </DropdownItem>
                <DropdownItem
                  className={styles.item}
                  data-testid="share-button-mobile"
                  onClick={() => share(episode)}
                >
                  <ShareIcon className={styles.icon} /> {L.MENTIONS_SHARE}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>

      {description_has_mention &&
        trimmed_description_mention &&
        description_mention && (
          <div className={styles.description}>
            <Typography
              data-testid="episode-description"
              className="highlight-match"
              component="span"
              variant="body-2"
              dangerouslySetInnerHTML={{
                __html: showFullDescription
                  ? description_mention
                  : trimmed_description_mention,
              }}
            />

            {(description_mention.length || 0) >
              trimmed_description_mention.length && (
              <ExpandDescription
                showFullDescription={showFullDescription}
                onClick={() => setShowFullDescription(!showFullDescription)}
              />
            )}
          </div>
        )}

      {is_paywalled && (
        <span data-testid="episode-paywalled">
          {L.MENTIONS_CONTEXT_PAYWALLED}
        </span>
      )}
      {mentions.length
        ? renderBody(source, mentions[0].context, Boolean(defaultOpen), title)
        : null}
      <hr />
    </div>
  );
};

export default EpisodeMobile;
